import React, { useState, useEffect, useMemo, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Plus, Search, Filter, DollarSign, Calendar, AlertTriangle,
    ChevronRight, Sparkles, GripVertical, X, MoreHorizontal, List, BarChart3
} from 'lucide-react';
import DealModal from '../components/deals/DealModal';
import DisabledReason, { deniedReason } from '../components/DisabledReason';
import SortableHeader, { useTableSort, useColumnFilters, usePagination, TablePagination } from '../components/table/SortableHeader';
import usePipelineDefinition from '../hooks/usePipelineDefinition';
import { errorText } from '../lib/apiErrors';
import { personLabel } from '../lib/teammateLabels';
import { dealStages, dealStageMeta, unmappedDeals, isClosedDealStage, isUnknownStage } from '../lib/dealStages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// POST /deals needs `deals:create`; without it the add buttons stay visible but disabled, with this as their title and
// as their accessible description. While permissions are still unknown they are disabled and say nothing.
const CREATE_DEAL_REASON = 'You do not have permission to create deals.';

const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;

// `showStage` prints the stored stage on the card: cards in the "Unmapped stage" column carry a value the definition does not know.
function DealCard({ deal, onEdit, onDragStart, showStage }) {
    const navigate = useNavigate();
    // #882: the card shows the first linked contact only; `linked_contact_count` counts the rest.
    const linked = deal.linked_contact_count || 0;
    const contact = linked > 0 ? deal.contact_preview?.[0] : null;
    const contactName = contact && (contact.full_name || contact.email || 'Unnamed contact');

    return (
        <div
            className="bg-white rounded-lg border border-stone-200 p-3 cursor-pointer hover:border-stone-400 hover:shadow-md transition-all group relative"
            draggable
            onDragStart={e => onDragStart(e, deal)}
            onClick={() => navigate(`/deals/${deal.deal_id}`)}
            data-testid={`deal-card-${deal.deal_id}`}
            data-nav-to="DealDetail"
        >
            <div className="flex items-start justify-between mb-2">
                <p className="text-sm font-medium text-stone-900 leading-snug pr-2" data-field="deal.title">{deal.title}</p>
                <button onClick={e => { e.stopPropagation(); onEdit(deal); }} className="p-1 rounded hover:bg-stone-100 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <MoreHorizontal className="w-3.5 h-3.5 text-stone-400" />
                </button>
            </div>
            {deal.account_name && <p className="text-xs text-stone-400 mb-2">{deal.account_name}</p>}
            {deal.days_in_stage != null && <p className="text-xs text-stone-400 mb-2" data-testid={`deal-days-in-stage-${deal.deal_id}`}>{deal.days_in_stage}d in stage</p>}
            {showStage && <p className="text-xs text-amber-700 mb-2">{deal.stage || '—'}</p>}
            {contact && (
                <div className="mb-2 pt-2 border-t border-stone-100 space-y-0.5" data-testid={`deal-card-contact-${deal.deal_id}`}>
                    <p className="text-xs text-stone-700 truncate">{contact.title ? `${contactName} · ${contact.title}` : contactName}</p>
                    {contact.email && contact.email !== contactName && <p className="text-xs text-stone-400 truncate">{contact.email}</p>}
                    {linked > 1 && <p className="text-xs text-stone-400">{`+${linked - 1} linked contact${linked === 2 ? '' : 's'}`}</p>}
                    <p className="text-xs text-stone-500">Deal Owner: {personLabel(deal.owner_id, deal.deal_owner_name) || 'Unassigned'}</p>
                    {linked === 1 && (
                        <p className="text-xs text-stone-500">Contact Owner: {personLabel(contact.contact_owner_id, contact.contact_owner_name)
                            || <span className="text-stone-300" title="No Contact Owner">—</span>}</p>
                    )}
                </div>
            )}
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-stone-700">{deal.value ? formatVal(deal.value) : '—'}</span>
                {deal.ai_risk_score !== undefined && !isClosedDealStage(deal.stage) && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${deal.ai_risk_score > 60 ? 'risk-high' : deal.ai_risk_score > 30 ? 'risk-medium' : 'risk-low'}`}>
                        {deal.ai_risk_score > 60 && <AlertTriangle className="w-3 h-3" />}
                        {deal.ai_risk_score}
                    </span>
                )}
            </div>
            {deal.expected_close_date && (
                <p className="text-xs text-stone-400 mt-1.5 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{deal.expected_close_date}
                </p>
            )}
        </div>
    );
}

// One Kanban column. The "Unmapped stage" column omits the drag handlers and `onAdd`: cards leave it, nothing is dropped into it.
// `addDisabled` shows "Add deal" disabled and inert, with `addDisabledReason` (when there is one) as its title and description.
function KanbanColumn({ testId, label, color, deals, isOver, onDragOver, onDrop, onAdd, addDisabled, addDisabledReason, onEdit, onDragStart, showStage }) {
    const total = deals.reduce((s, d) => s + (d.value || 0), 0);
    const reasonId = useId();
    return (
        <div
            className={`w-64 flex flex-col bg-stone-50 rounded-xl border ${isOver ? 'border-stone-300 bg-stone-100/30' : 'border-stone-200'} transition-colors`}
            onDragOver={onDragOver}
            onDrop={onDrop}
            data-testid={testId}
        >
            {/* Column header */}
            <div className="p-3 border-b border-stone-200">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-xs font-semibold text-stone-700">{label}</span>
                    </div>
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-700">
                        {deals.length}
                    </span>
                </div>
                <p className="text-xs text-stone-400 font-medium">{formatVal(total)}</p>
            </div>
            {/* Cards */}
            <div className="p-2 flex-1 space-y-2 kanban-col">
                {deals.map(deal => (
                    <DealCard key={deal.deal_id} deal={deal} onEdit={onEdit} onDragStart={onDragStart} showStage={showStage} />
                ))}
            </div>
            {onAdd && (
                <>
                    <button type="button" onClick={addDisabled ? undefined : onAdd} disabled={addDisabled} title={addDisabledReason}
                        aria-describedby={addDisabledReason ? reasonId : undefined}
                        className="m-2 p-2 rounded-lg border border-dashed border-stone-300 text-stone-400 hover:text-stone-700 hover:border-stone-400 text-xs flex items-center justify-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-stone-400 disabled:hover:border-stone-300">
                        <Plus className="w-3.5 h-3.5" /> Add deal
                    </button>
                    <DisabledReason id={reasonId} reason={addDisabledReason} />
                </>
            )}
        </div>
    );
}

export default function Pipeline() {
    const { getAuthHeader } = useAuth();
    const { hasEffectivePermission, permissionsUnknown } = usePermissions();
    const canCreateDeal = hasEffectivePermission('deals:create');
    const createDealReason = deniedReason(canCreateDeal, permissionsUnknown, CREATE_DEAL_REASON);
    const createReasonId = useId();
    const navigate = useNavigate();
    // null on a failed fetch: dealStages() then falls back to the defaults, so the board is never blank
    const { definition, loading: definitionLoading, error: definitionError, reload: reloadDefinition } = usePipelineDefinition('sales');
    const stages = dealStages(definition);
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editDeal, setEditDeal] = useState(null);
    const [view, setView] = useState('kanban');
    const [draggedDeal, setDraggedDeal] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const dRes = await axios.get(`${API}/deals`, { headers: getAuthHeader(), params: { include: 'card_context' } });
            setDeals(Array.isArray(dRes.data) ? dRes.data : (dRes.data?.data || []));
        } catch (err) { toast.error(errorText(err, 'Failed to load deals')); } finally { setLoading(false); }
    };

    const handleSave = (saved, { linksChanged = false } = {}) => {
        setDeals(prev => {
            const idx = prev.findIndex(d => d.deal_id === saved.deal_id);
            // merge: the PUT response has no card_context fields, and the card keeps its contact preview
            if (idx >= 0) { const u = [...prev]; u[idx] = { ...u[idx], ...saved }; return u; }
            return [saved, ...prev];
        });
        // #906: a linked contact changed, so the card's preview and count come from a fresh read
        if (linksChanged) loadData();
    };

    // Drag & drop
    const handleDragStart = (e, deal) => {
        setDraggedDeal(deal);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e, stage) => {
        e.preventDefault();
        setDragOverStage(stage);
    };
    const handleDrop = async (e, stage) => {
        e.preventDefault();
        if (!draggedDeal || draggedDeal.stage === stage) { setDraggedDeal(null); setDragOverStage(null); return; }
        try {
            const meta = dealStageMeta(definition, stage);
            const res = await axios.put(`${API}/deals/${draggedDeal.deal_id}`, { stage, probability: meta.probability }, { headers: getAuthHeader() });
            setDeals(prev => prev.map(d => d.deal_id === draggedDeal.deal_id ? { ...d, ...res.data } : d));
            toast.success(`Moved to ${meta.label}`);
        } catch (err) {
            toast.error(errorText(err, 'Failed to update stage'));
            // a stage an admin has since removed: re-read the definition so the board shows the current columns
            if (isUnknownStage(err)) reloadDefinition();
        } finally { setDraggedDeal(null); setDragOverStage(null); }
    };

    const filteredDeals = search ? deals.filter(d => d.title?.toLowerCase().includes(search.toLowerCase()) || d.account_name?.toLowerCase().includes(search.toLowerCase())) : deals;
    const dealsByStage = (stage) => filteredDeals.filter(d => d.stage === stage);
    // not while the definition loads: a deal in an admin-added stage would flash in the Unmapped column
    const unmapped = definitionLoading ? [] : unmappedDeals(definition, filteredDeals);
    const openEdit = (deal) => { setEditDeal(deal); setShowModal(true); };

    // List view sort + column filter
    // the Stage column filters and sorts by what it shows (the label), not by the stored key
    const listDeals = useMemo(() => filteredDeals.map(d => ({ ...d, stage_label: dealStageMeta(definition, d.stage).label })), [filteredDeals, definition]);
    const DEAL_COLS = [
        { key: 'title', label: 'Deal' },
        { key: 'stage_label', label: 'Stage' },
        { key: 'value', label: 'Value' },
        { key: 'expected_close_date', label: 'Close Date' },
        { key: 'ai_risk_score', label: 'Risk' },
        { key: 'probability', label: 'Probability' },
    ];
    const { columnFilters: dealColFilters, setColumnFilter: setDealColFilter, filteredData: colFilteredDeals } = useColumnFilters(listDeals);
    const { sortColumn: dealSortCol, sortDirection: dealSortDir, handleSort: handleDealSort, sortedData: sortedDeals } = useTableSort(colFilteredDeals, { numericKeys: ['value', 'ai_risk_score', 'probability'] });
    const { page: dealPage, pageSize: dealPageSize, setPage: setDealPage, setPageSize: setDealPageSize, pageData: pagedDeals, totalPages: dealTotalPages, total: dealTotal } = usePagination(sortedDeals, 25);
    const totalPipeline = deals.filter(d => !isClosedDealStage(d.stage)).reduce((s, d) => s + (d.value || 0), 0);

    return (
        <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                    <div className="relative min-w-48 max-w-sm flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input type="text" placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)}
                            data-testid="pipeline-search-input"
                            data-field="deal.search_query"
                            className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 bg-white" />
                    </div>
                    <div className="flex rounded-lg border border-stone-200 overflow-hidden bg-white">
                        <button onClick={() => setView('kanban')} data-testid="kanban-view-btn"
                            data-action="pipeline.view_kanban"
                            className={`px-3 py-2 text-sm flex items-center gap-2 transition-colors ${view === 'kanban' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                            <GripVertical className="w-4 h-4" />Kanban
                        </button>
                        <button onClick={() => setView('list')} data-testid="list-view-btn"
                            data-action="pipeline.view_list"
                            className={`px-3 py-2 text-sm flex items-center gap-2 transition-colors ${view === 'list' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-50'}`}>
                            <List className="w-4 h-4" />List
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-sm text-stone-600">
                        Pipeline: <span className="font-bold text-stone-900" data-field="deal.total_pipeline_value">{formatVal(totalPipeline)}</span>
                    </div>
                    <button type="button" onClick={() => { setEditDeal(null); setShowModal(true); }} data-testid="new-deal-btn"
                        data-action="deal.create"
                        disabled={!canCreateDeal} title={createDealReason}
                        aria-describedby={createDealReason ? createReasonId : undefined}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-stone-900">
                        <Plus className="w-4 h-4" /> New Deal
                    </button>
                    <DisabledReason id={createReasonId} reason={createDealReason} />
                </div>
            </div>

            {definitionError && !definition && (
                <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800" data-testid="pipeline-definition-error">
                    <span>Your pipeline's stage list could not be loaded, so the default stages are shown. Dragging a deal into a stage your admin has renamed or removed may be refused.</span>
                    <button onClick={reloadDefinition} className="font-semibold underline hover:no-underline flex-shrink-0">Retry</button>
                </div>
            )}

            {/* Kanban Board */}
            {view === 'kanban' && (
                <div className="overflow-x-auto pb-4">
                    <div className="flex gap-4 min-w-max">
                        {stages.map(stage => (
                            <KanbanColumn key={stage.key}
                                testId={`kanban-col-${stage.key.replace(/\s+/g, '-').toLowerCase()}`}
                                label={stage.label}
                                color={stage.color}
                                deals={dealsByStage(stage.key)}
                                isOver={dragOverStage === stage.key}
                                onDragOver={e => handleDragOver(e, stage.key)}
                                onDrop={e => handleDrop(e, stage.key)}
                                onAdd={() => { setEditDeal({ stage: stage.key }); setShowModal(true); }}
                                addDisabled={!canCreateDeal}
                                addDisabledReason={createDealReason}
                                onEdit={openEdit}
                                onDragStart={handleDragStart} />
                        ))}
                        {unmapped.length > 0 && (
                            <KanbanColumn key="unmapped"
                                testId="kanban-col-unmapped"
                                label="Unmapped stage"
                                color={dealStageMeta(definition, unmapped[0].stage).color}
                                deals={unmapped}
                                onEdit={openEdit}
                                onDragStart={handleDragStart}
                                showStage />
                        )}
                    </div>
                </div>
            )}

            {/* List View */}
            {view === 'list' && (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="pipeline-list-view">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-stone-50 border-b border-stone-200">
                                {DEAL_COLS.map(col => (
                                    <SortableHeader
                                        key={col.key}
                                        label={col.label}
                                        columnKey={col.key}
                                        sortColumn={dealSortCol}
                                        sortDirection={dealSortDir}
                                        onSort={handleDealSort}
                                        filterValue={dealColFilters[col.key]}
                                        onFilter={setDealColFilter}
                                        allData={listDeals}
                                    />
                                ))}
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {pagedDeals.map(d => {
                                const meta = dealStageMeta(definition, d.stage);
                                return (
                                    <tr key={d.deal_id} className="hover:bg-stone-50 cursor-pointer transition-colors group" onClick={() => navigate(`/deals/${d.deal_id}`)}>
                                        <td className="px-4 py-3">
                                            <p className="text-sm font-medium text-stone-900">{d.title}</p>
                                            <p className="text-xs text-stone-400">{d.account_name}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium bg-stone-100 text-stone-700">
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />{meta.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-medium text-sm text-stone-700">{d.value ? formatVal(d.value) : '—'}</td>
                                        <td className="px-4 py-3 text-sm text-stone-500">{d.expected_close_date || '—'}</td>
                                        <td className="px-4 py-3">
                                            {d.ai_risk_score !== undefined ? (
                                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${d.ai_risk_score > 60 ? 'risk-high' : d.ai_risk_score > 30 ? 'risk-medium' : 'risk-low'}`}>
                                                    {d.ai_risk_score}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-stone-1000 rounded-full" style={{ width: `${d.probability || 0}%` }} />
                                                </div>
                                                <span className="text-xs text-stone-500">{d.probability || 0}%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <button onClick={e => { e.stopPropagation(); openEdit(d); }} className="p-1.5 rounded-lg hover:bg-stone-100 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <MoreHorizontal className="w-4 h-4 text-stone-400" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <TablePagination
                        page={dealPage}
                        totalPages={dealTotalPages}
                        total={dealTotal}
                        pageSize={dealPageSize}
                        onPageChange={setDealPage}
                        onPageSizeChange={setDealPageSize}
                    />
                </div>
            )}

            <DealModal open={showModal} onClose={() => { setShowModal(false); setEditDeal(null); }} deal={editDeal} onSave={handleSave} definition={definition} onStageRejected={reloadDefinition} />
        </div>
    );
}
